import React from 'react';
import './Step5Trigger.css';
import ScheduleZoneField, { defaultScheduleZone } from '../../shared/ScheduleZoneField';
import { scheduleLabel } from '../../../../utils/scheduleLabel';
import AutomationNameAndDescriptionFields from './AutomationNameAndDescriptionFields';

const Step5Trigger = ({ formData, onSetFormData }) => {
  const { trigger } = formData;
  const scheduleType = trigger.scheduleType || 'scheduled';

  const handleScheduleTypeChange = (type) => {
    onSetFormData({
      ...formData,
      trigger: { ...trigger, scheduleType: type }
    });
  };

  const handleChange = (field, value) => {
    onSetFormData({
      ...formData,
      trigger: { ...trigger, [field]: value }
    });
  };

  const formatScheduleDisplay = () => {
    if (scheduleType === 'on-demand') {
      return 'Run on demand only';
    }
    return scheduleLabel(trigger, formData.timezone || defaultScheduleZone());
  };

  return (
    <div className="step-content">
      <h3 className="step-title">Schedule and review</h3>
      <p className="step-subtitle">Set the schedule and review your automation configuration.</p>

      <div className="review-summary">
        <div className="review-item">
          <span className="review-label">Template type:</span>
          <span className="review-value">{formData.type ? formData.type.charAt(0).toUpperCase() + formData.type.slice(1) : 'N/A'}</span>
        </div>
        <div className="review-item">
          <span className="review-label">Source:</span>
          <div className="review-nested">
            <div className="review-item">
              <span className="review-label">Connection:</span>
              <span className="review-value">{formData.source?.display_name || 'None'}</span>
            </div>
            <div className="review-item">
              <span className="review-label">Path:</span>
              <span className="review-value">{formData.path || formData.rootPrefix || 'None'}</span>
            </div>
          </div>
        </div>
        <div className="review-item">
          <span className="review-label">Target dataset:</span>
          <div className="review-nested">
            <div className="review-item">
              <span className="review-label">Action:</span>
              <span className="review-value">{formData.datasetAction === 'create' ? 'Create new dataset' : 'Update existing dataset'}</span>
            </div>
            {formData.datasetAction === 'create' && (
              <>
                <div className="review-item">
                  <span className="review-label">Name:</span>
                  <span className="review-value">
                    {formData.createPerFileDataset
                      ? 'One dataset per file (named after the filename)'
                      : (formData.datasetName || 'N/A')}
                  </span>
                </div>
              </>
            )}
            {formData.datasetAction === 'update' && formData.datasetId && (
              <div className="review-item">
                <span className="review-label">Dataset:</span>
                <span className="review-value">{formData.datasetName || 'N/A'}</span>
              </div>
            )}
          </div>
        </div>
        <div className="review-item">
          <span className="review-label">Trigger details:</span>
          <div className="review-nested">
            <div className="review-item">
              <span className="review-label">Schedule:</span>
              <span className="review-value">{formatScheduleDisplay()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="info-banner">
        <p>This automation will keep the target dataset up to date from the selected connection on the chosen schedule.</p>
      </div>

      <div className="form-field">
        <label>Schedule</label>
        <div className="radio-group">
          <label className="resui-radio">
            <input
              type="radio"
              className="resui-radio-input"
              name="scheduleType"
              value="scheduled"
              checked={scheduleType === 'scheduled'}
              onChange={() => handleScheduleTypeChange('scheduled')}
            />
            <span className="resui-radio-mark" />
            <span className="resui-radio-label">Run on schedule</span>
          </label>
          <label className="resui-radio">
            <input
              type="radio"
              className="resui-radio-input"
              name="scheduleType"
              value="on-demand"
              checked={scheduleType === 'on-demand'}
              onChange={() => handleScheduleTypeChange('on-demand')}
            />
            <span className="resui-radio-mark" />
            <span className="resui-radio-label">Run on demand only</span>
          </label>
        </div>
      </div>

      {scheduleType === 'scheduled' && (
        <>
          <div className="form-field">
            <label>Frequency</label>
            <div className="frequency-options">
              {['Daily', 'Weekly', 'Monthly'].map((freq) => (
                <label key={freq} className="resui-radio">
                  <input
                    type="radio"
                    className="resui-radio-input"
                    name="frequency"
                    value={freq.toLowerCase()}
                    checked={trigger.frequency === freq.toLowerCase()}
                    onChange={(e) => handleChange('frequency', e.target.value)}
                  />
                  <span className="resui-radio-mark" />
                  <span className="resui-radio-label">{freq}</span>
                </label>
              ))}
            </div>
          </div>

          {trigger.frequency === 'weekly' && (
            <div className="form-field">
              <label>Day of week</label>
              <select
                className="resui-select"
                value={trigger.dayOfWeek || 'monday'}
                onChange={(e) => handleChange('dayOfWeek', e.target.value)}
              >
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>
          )}

          <div className="form-field">
            <label>Time</label>
            <input
              type="time"
              className="resui-input trigger-time-input"
              value={trigger.time || '08:00'}
              onChange={(e) => handleChange('time', e.target.value)}
            />
          </div>

          <ScheduleZoneField
            value={formData.timezone || defaultScheduleZone()}
            onChange={(timezone) => onSetFormData({ ...formData, timezone })}
          />
        </>
      )}

      <AutomationNameAndDescriptionFields formData={formData} onSetFormData={onSetFormData} />
    </div>
  );
};

export default Step5Trigger;
